import { useGetOne } from "ra-core";
import React from "react";

// QTN-5: users sometimes type placeholder text ("Not provided yet", "N/A", "-")
// into the billing/shipping address fields. Treat those as empty so the print
// falls back to the assembled address (or "No address provided.") instead of
// rendering the placeholder verbatim on the customer-facing document.
const ADDRESS_PLACEHOLDER_RE =
  /^\s*(not\s*provided(\s*yet)?|to\s*be\s*provided|tbd|n\.?\/?a\.?|none|nil|null|-+|\.+)\s*$/i;
const cleanAddress = (value?: string | null): string => {
  const v = (value ?? "").trim();
  return v && !ADDRESS_PLACEHOLDER_RE.test(v) ? v : "";
};

interface AddressBlockProps {
  label: string;
  companyId?: string | number;
  vendorId?: string | number;
  companyInfoId?: string | number; // For "From" address - our company
  addressOverride?: React.ReactNode;
  useShipping?: boolean;
  useBilling?: boolean;
  children?: React.ReactNode;
}

export const AddressBlock = ({
  label,
  companyId,
  vendorId,
  companyInfoId,
  addressOverride,
  useShipping,
  useBilling,
  children,
}: AddressBlockProps) => {
  // Fetch company if ID provided
  const { data: company, isPending: isCompanyPending } = useGetOne(
    "companies",
    { id: companyId },
    { enabled: !!companyId }
  );

  // Fetch vendor if ID provided
  const { data: vendor, isPending: isVendorPending } = useGetOne(
    "vendors",
    { id: vendorId },
    { enabled: !!vendorId }
  );

  // Fetch company_info if ID provided (for "From" address)
  const { data: companyInfo, isPending: isCompanyInfoPending } = useGetOne(
    "company_info",
    { id: companyInfoId },
    { enabled: !!companyInfoId }
  );

  const isPending = isCompanyPending || isVendorPending || isCompanyInfoPending;

  if (addressOverride) {
    return (
      <div className="address-block flex-1 min-w-[200px] mb-6 md:mb-0 p-3 pt-2">
        <h4 className="font-bold text-[11px] uppercase tracking-wider mb-2 border-b border-gray-300 pb-1 bg-gray-100 py-1 px-3 -mx-3 -mt-2">
          {label} :
        </h4>
        <div className="text-[11px] leading-relaxed space-y-1">
          {addressOverride}
          {children}
        </div>
      </div>
    );
  }

  const record = company || vendor || companyInfo;
  const isVendor = !!vendor;
  const isCompanyInfo = !!companyInfo;

  if (record) {
    let addressValue = "";
    
    if (isCompanyInfo) {
        // Company Info (our company) address logic
        const parts = [
            record.address,
            [record.state].filter(Boolean).join(", "),
        ].filter(Boolean);
        addressValue = parts.join("\n");
    } else if (!isVendor) {
        // Customer Company address logic
        const shipping = cleanAddress(record.shipping_address);
        const billing = cleanAddress(record.billing_address);
        if (useShipping && shipping) {
            addressValue = shipping;
        } else if (useBilling && billing) {
            addressValue = billing;
        } else {
            const parts = [
                cleanAddress(record.address),
                [record.city, record.state, record.zipcode].filter(Boolean).join(", "),
                record.country
            ].filter(Boolean);
            addressValue = parts.join("\n");
        }
    } else {
        // Vendor address logic
        const parts = [
            record.address,
            [record.city, record.state].filter(Boolean).join(", "),
            record.country
        ].filter(Boolean);
        addressValue = parts.join("\n");
    }

    // Standardize GST and Phone field access
    const gst = record.gst_number || record.gstin || record.tax_identifier;
    const phone = record.phone || record.phone_number;

    return (
      <div className="address-block flex-1 min-w-[200px] mb-6 md:mb-0 p-3 pt-2">
        <h4 className="font-bold text-[11px] uppercase tracking-wider mb-2 border-b border-gray-300 pb-1 bg-gray-100 py-1 px-3 -mx-3 -mt-2">
          {label} :
        </h4>
        <div className="text-[11px] leading-relaxed space-y-1">
          <div className="font-bold flex items-center gap-1 mb-1 text-[12px]">
            {record.name}
          </div>
          <div className="whitespace-pre-wrap">{addressValue || "No address provided."}</div>
          
          {(gst || phone || record.email) && (
            <div className="mt-2 pt-2 text-[11px]">
              {gst && <div><span className="font-bold min-w-[90px] inline-block">GSTIN :</span> {gst}</div>}
              {phone && <div><span className="font-bold min-w-[90px] inline-block">Phone :</span> {phone}</div>}
              {record.email && <div><span className="font-bold min-w-[90px] inline-block">E-mail :</span> {record.email}</div>}
            </div>
          )}
          {children}
        </div>
      </div>
    );
  }

  if (isPending) {
     return (
        <div className="address-block flex-1 min-w-[200px] mb-6 md:mb-0 p-3 pt-2">
            <h4 className="font-bold text-[11px] uppercase tracking-wider mb-2 border-b border-gray-300 pb-1 bg-gray-100 py-1 px-3 -mx-3 -mt-2">
                {label} :
            </h4>
            <div className="animate-pulse flex flex-col gap-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
        </div>
     );
  }

  return (
    <div className="address-block flex-1 min-w-[200px] mb-6 md:mb-0 p-3 pt-2">
      <h4 className="font-bold text-[11px] uppercase tracking-wider mb-2 border-b border-gray-300 pb-1 bg-gray-100 py-1 px-3 -mx-3 -mt-2">
        {label} :
      </h4>
      <div className="text-[11px] text-muted-foreground italic">
        Not specified
      </div>
    </div>
  );
};
